import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useBilling, parseTierLimitError } from "../lib/billing";
import { toast } from "sonner";
import { Shield, UserCircle2, Trash2, Mail, Check, X, ArrowUp, ArrowDown, Send, Copy } from "lucide-react";

export default function Team() {
  const { user } = useAuth();
  const { openUpgradeModal } = useBilling();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    const { data } = await api.get("/team");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const isAdmin = user?.role === "admin";

  const showDelivery = (delivery, email) => {
    if (!delivery) return;
    if (delivery.sent) {
      toast.success(`Invite emailed to ${email}`);
    } else if (delivery.skipped) {
      toast.warning("Invite saved, but email isn't configured. Share the sign-in link manually.");
    } else if (delivery.error) {
      // Resend rejects unverified test recipients with a 403 — show a useful hint
      const msg = String(delivery.error);
      if (msg.includes("verify a domain") || msg.includes("testing emails")) {
        toast.warning(`Invite saved. Resend rejected the email: it's in test mode and only delivers to your own verified address. Verify a domain at resend.com/domains to invite anyone.`, { duration: 8000 });
      } else {
        toast.warning(`Invite saved, but email failed: ${msg}`);
      }
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      const { data: resp } = await api.post("/team/invite", {
        email: email.trim(),
        origin_url: window.location.origin,
      });
      showDelivery(resp.email_delivery, email.trim());
      setEmail("");
      load();
    } catch (err) {
      const tierErr = parseTierLimitError(err);
      if (tierErr) {
        openUpgradeModal(tierErr);
      } else {
        toast.error(err?.response?.data?.detail || "Failed");
      }
    } finally { setSending(false); }
  };

  const resendInvite = async (id, addr) => {
    try {
      const { data: resp } = await api.post(`/team/invite/${id}/resend`, {
        origin_url: window.location.origin,
      });
      showDelivery(resp.email_delivery, addr);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resend failed");
    }
  };

  const copyInviteLink = (addr) => {
    const url = `${window.location.origin}/?invited=1`;
    navigator.clipboard?.writeText(url);
    toast.success(`Link copied. Send to ${addr} however you like.`);
  };

  const cancelInvite = async (id) => {
    if (!window.confirm("Cancel this invite?")) return;
    await api.delete(`/team/invite/${id}`);
    toast.success("Invite cancelled");
    load();
  };

  const setRole = async (mid, role) => {
    try {
      await api.put(`/team/members/${mid}/role`, { role });
      toast.success(`Role updated to ${role}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (mid) => {
    if (!window.confirm("Remove this member from the shop?")) return;
    try {
      await api.delete(`/team/members/${mid}`);
      toast.success("Member removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  if (!data) return <div className="font-mono uppercase tracking-widest text-sm">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="team-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A]">Crew Roster</div>
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tight">Team</h1>
      </div>

      {!isAdmin && (
        <div className="alert-bar !bg-[#111]">
          <Shield size={14} className="inline mr-2"/> Staff view · Ask your admin to change roles or invite new members.
        </div>
      )}

      {isAdmin && (
        <form onSubmit={invite} className="brutal-card p-6 grid grid-cols-1 md:grid-cols-[1fr_160px] gap-3 items-end">
          <div>
            <label className="brutal-label">Invite by email</label>
            <div className="flex items-center gap-2">
              <Mail size={16}/>
              <input className="brutal-input" placeholder="teammate@example.com"
                type="email" value={email} onChange={(e)=>setEmail(e.target.value)}
                data-testid="invite-email-input" required/>
            </div>
          </div>
          <button className="brutal-btn" disabled={sending} data-testid="send-invite-btn">
            {sending ? "Sending…" : "Send Invite"}
          </button>
        </form>
      )}

      {isAdmin && data.invites?.length > 0 && (
        <div className="brutal-card p-6">
          <h2 className="font-heading text-xl font-black uppercase mb-4">Pending Invites</h2>
          <div className="overflow-x-auto">
            <table className="brutal-table">
              <thead><tr><th>Email</th><th>Role</th><th>Sent</th><th>Actions</th></tr></thead>
              <tbody>
                {data.invites.map((i) => (
                  <tr key={i.invite_id} data-testid={`invite-row-${i.invite_id}`}>
                    <td className="font-mono text-xs">{i.email}</td>
                    <td><span className="brutal-badge">{i.role}</span></td>
                    <td className="font-mono text-[10px] text-[#4A4A4A]">{new Date(i.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <button className="brutal-btn brutal-btn-secondary !py-1 !px-2 text-xs"
                          onClick={()=>resendInvite(i.invite_id, i.email)}
                          title="Resend email"
                          data-testid={`resend-invite-${i.invite_id}`}>
                          <Send size={12}/>
                        </button>
                        <button className="brutal-btn brutal-btn-secondary !py-1 !px-2 text-xs"
                          onClick={()=>copyInviteLink(i.email)}
                          title="Copy sign-in link"
                          data-testid={`copy-invite-${i.invite_id}`}>
                          <Copy size={12}/>
                        </button>
                        <button className="brutal-btn brutal-btn-danger !py-1 !px-2 text-xs"
                          onClick={()=>cancelInvite(i.invite_id)}
                          title="Cancel invite"
                          data-testid={`cancel-invite-${i.invite_id}`}>
                          <X size={12}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="brutal-card p-6">
        <h2 className="font-heading text-xl font-black uppercase mb-4">Members · {data.members.length}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.members.map((m) => (
            <div key={m.user_id} className="brutal-card-sm p-4 flex items-center gap-4" data-testid={`member-${m.user_id}`}>
              {m.picture ? (
                <img src={m.picture} alt="" className="w-12 h-12 border-2 border-black object-cover"/>
              ) : (
                <div className="w-12 h-12 bg-[#EAE7DF] border-2 border-black flex items-center justify-center">
                  <UserCircle2 size={20}/>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold truncate">{m.name || m.email}</div>
                <div className="font-mono text-[10px] text-[#4A4A4A] truncate">{m.email}</div>
                <div className="mt-1">
                  <span className={`brutal-badge ${m.role === "admin" ? "!bg-[#C2410C] !text-white" : ""}`}>
                    {m.role === "admin" ? <Shield size={10} className="inline mr-1"/> : <Check size={10} className="inline mr-1"/>}
                    {m.role}
                  </span>
                  {m.user_id === data.me.user_id && <span className="brutal-badge ml-2">You</span>}
                </div>
              </div>
              {isAdmin && m.user_id !== data.me.user_id && (
                <div className="flex flex-col gap-1">
                  {m.role === "staff" ? (
                    <button className="brutal-btn brutal-btn-secondary !py-1 !px-2 text-xs"
                      onClick={()=>setRole(m.user_id, "admin")}
                      data-testid={`promote-${m.user_id}`}>
                      <ArrowUp size={12}/>
                    </button>
                  ) : (
                    <button className="brutal-btn brutal-btn-secondary !py-1 !px-2 text-xs"
                      onClick={()=>setRole(m.user_id, "staff")}
                      data-testid={`demote-${m.user_id}`}>
                      <ArrowDown size={12}/>
                    </button>
                  )}
                  <button className="brutal-btn brutal-btn-danger !py-1 !px-2 text-xs"
                    onClick={()=>remove(m.user_id)}
                    data-testid={`remove-${m.user_id}`}>
                    <Trash2 size={12}/>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
